import { MAX_DIRECT_ASSIGNMENTS_PER_USER } from '@scale/shared/defaults';
import { MAX_ELIGIBLE_USERS_FOR_PROJECT_INVITE } from '@scale/shared/outlier/constants';

/**
 * Valid duration options for invite expiration (in days)
 */
export const VALID_DURATION_DAYS = [1, 2, 3] as const;

/**
 * Type for insert order option
 */
type InsertOrderOption = {
  id: string;
  name: string;
  value: number | undefined;
};

/**
 * Options for assignment rank / insert order
 * Controls where the assignment will be inserted into the users' assignment list
 */
export const INSERT_ORDER_OPTIONS: InsertOrderOption[] = [
  {
    id: 'undefined',
    name: 'Push to end (default)',
    value: undefined,
  },
  {
    id: '0',
    name: 'Top',
    value: 0,
  },
  {
    id: '1',
    name: 'Second',
    value: 1,
  },
  {
    id: '2',
    name: 'Bottom',
    value: MAX_DIRECT_ASSIGNMENTS_PER_USER - 1,
  },
];

/**
 * Type for disabled state configuration
 */
export type DisabledState = {
  reason: string;
  buttonText: string;
};

/**
 * Disabled states for the invite button with their reasons and display text
 */
export const DISABLED_STATES: Record<string, DisabledState> = {
  NO_ELIGIBLE_CONTRIBUTORS: {
    reason: 'No eligible contributors to invite',
    buttonText: 'No eligible contributors',
  },
  NO_CONTRIBUTORS: {
    reason: 'No contributors added',
    buttonText: 'Add Contributors',
  },
  LOADING: {
    reason: 'Loading eligibility data',
    buttonText: 'Loading...',
  },
  CREATING: {
    reason: 'Creating invite batch',
    buttonText: 'Creating...',
  },
  TOO_MANY: {
    reason: `Cannot invite more than ${MAX_ELIGIBLE_USERS_FOR_PROJECT_INVITE} contributors at once`,
    buttonText: 'Too Many Contributors',
  },
  NO_PERMISSION: {
    reason: "You don't have permission to send project invites",
    buttonText: 'No Permission',
  },
};

/**
 * Converts insert order option id to numeric value
 */
export const parseInsertOrderValue = (id: string): number | undefined => {
  if (id === 'undefined') return undefined;
  return parseInt(id);
};

/**
 * Converts insert order numeric value to option id
 */
export const insertOrderValueToId = (value: number | undefined): string => {
  if (value === undefined) return 'undefined';
  return value.toString();
};
